/**
 * EnhancedCard - Backward compatibility wrapper
 * Maps old EnhancedCard variants to new UnifiedCard component
 * 
 * This wrapper maintains backward compatibility for existing imports:
 * - GlassCard → UnifiedCard variant="glass"
 * - GradientCard → UnifiedCard variant="gradient"
 * - ElevatedCard → UnifiedCard variant="elevated"
 * - BorderedCard → UnifiedCard variant="outlined"
 * - InteractiveCard → UnifiedCard variant="interactive"
 * - StatsCard → Uses StatCardContent
 * - CardHeader, CardFooter → Re-exported from UnifiedCard
 */

import React from 'react';
import UnifiedCard, { CardHeader as UnifiedCardHeader, CardFooter as UnifiedCardFooter, StatCardContent } from './UnifiedCard';
import { CardContent } from '@mui/material';

// Glass Card - frosted glass effect
export const GlassCard = ({ children, ...props }) => (
  <UnifiedCard variant="glass" {...props}>
    <CardContent sx={{ p: 3 }}>
      {children}
    </CardContent>
  </UnifiedCard>
);

// Gradient Card - gradient background
export const GradientCard = ({ children, gradient = 'primary', ...props }) => (
  <UnifiedCard variant="gradient" gradient={gradient} {...props}>
    <CardContent sx={{ p: 3 }}>
      {children}
    </CardContent>
  </UnifiedCard>
);

// Elevated Card - strong shadow
export const ElevatedCard = ({ children, ...props }) => (
  <UnifiedCard variant="elevated" {...props}>
    <CardContent sx={{ p: 3 }}>
      {children}
    </CardContent>
  </UnifiedCard>
);

// Bordered Card - outlined with hover effect
export const BorderedCard = ({ children, ...props }) => (
  <UnifiedCard variant="outlined" {...props}>
    <CardContent sx={{ p: 3 }}>
      {children}
    </CardContent>
  </UnifiedCard>
);

// Interactive Card - spotlight effect on hover
export const InteractiveCard = ({ children, ...props }) => (
  <UnifiedCard variant="interactive" {...props}>
    <CardContent sx={{ p: 3 }}>
      {children}
    </CardContent>
  </UnifiedCard>
);

// Stats Card - displays statistics with icon and trend
export const StatsCard = ({
  icon,
  title,
  value,
  trend,
  trendDirection = 'up',
  color = 'primary',
  variant = 'default',
  ...props
}) => {
  // Map old variant names to new ones
  const variantMap = {
    gradient: 'gradient',
    glass: 'glass',
    default: 'default',
  };

  return (
    <UnifiedCard variant={variantMap[variant] || 'default'} hover {...props}>
      <StatCardContent
        icon={icon}
        title={title}
        value={value}
        trend={trend}
        trendDirection={trendDirection}
        color={color}
        variant={variant}
      />
    </UnifiedCard>
  );
};

// Re-export CardHeader and CardFooter
export const CardHeader = UnifiedCardHeader;
export const CardFooter = UnifiedCardFooter;

// Default export with all variants
export default {
  GlassCard,
  GradientCard,
  ElevatedCard,
  BorderedCard,
  InteractiveCard,
  StatsCard,
  CardHeader,
  CardFooter,
};
